/**
 * UC-06: ProcessLoan
 * TOGAF Application ABB — take or repay a fixed-size loan without UI deps.
 */
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import {
  LOAN_AMOUNT,
  type ProcessLoanInput,
  type ProcessLoanOutput,
} from '../dto/EconomyDto';

export interface ProcessLoanDependencies {
  logger?: IAppLogger;
}

export class ProcessLoanUseCase {
  constructor(private readonly deps: ProcessLoanDependencies = {}) {}

  execute(input: ProcessLoanInput): ProcessLoanOutput {
    const { action, stats } = input;
    const loanAmount = input.loanAmount ?? LOAN_AMOUNT;

    if (action === 'take') {
      const updatedStats = {
        ...stats,
        budget: stats.budget + loanAmount,
        loanCount: stats.loanCount + 1,
      };

      this.deps.logger?.log('debug', 'loan.taken', { loanAmount });

      return {
        ok: true,
        stats: updatedStats,
        newsMessage: `Loan approved: +$${loanAmount.toLocaleString()}. Interest applied.`,
        newsType: 'neutral',
        sfx: 'money',
      };
    }

    if (stats.budget < loanAmount) {
      return {
        ok: false,
        stats,
        error: 'Insufficient budget to repay loan',
        sfx: 'error',
      };
    }

    if (stats.loanCount <= 0) {
      return {
        ok: false,
        stats,
        error: 'No active loans to repay',
        sfx: 'error',
      };
    }

    const updatedStats = {
      ...stats,
      budget: stats.budget - loanAmount,
      loanCount: stats.loanCount - 1,
    };

    this.deps.logger?.log('debug', 'loan.repaid', { loanAmount });

    return {
      ok: true,
      stats: updatedStats,
      newsMessage: 'Loan repaid. Interest reduced.',
      newsType: 'positive',
      sfx: 'money',
    };
  }
}